import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Button, Field, SelectField, Spinner, StatusBadge } from "@leylion/design-system";
import { PlatformDiagnosticAlert } from "./PlatformDiagnosticAlert";
import { toPlatformDiagnostic, type PlatformDiagnostic, type PlatformLanguage } from "./platform-diagnostics";
import {
  getPlatformAuditEvents,
  type PlatformAuditEvent,
  type PlatformAuditFilters
} from "./platform-audit-client";

const initialFilters: PlatformAuditFilters = { pageSize: 30 };
const hiddenDetailKey = /(password|token|secret|cookie|authorization|recoverycode|mfasecret)/i;
const auditRefreshEvent = "leylion-platform-audit-refresh";

type DetailRow = { key: string; value: string };
type Tone = "success" | "warning" | "danger" | "info" | "neutral";

function resultTone(result: string | null): Tone {
  const normalized = result?.toLowerCase() ?? "";
  if (["succeeded", "success", "revoked", "completed", "created"].includes(normalized)) return "success";
  if (["alreadyinactive", "skipped", "noop"].includes(normalized)) return "warning";
  if (["failed", "rejected", "denied"].includes(normalized)) return "danger";
  return result ? "info" : "neutral";
}

function detailRows(value: string): DetailRow[] {
  try {
    const parsed: unknown = JSON.parse(value);
    const rows: DetailRow[] = [];
    const visit = (current: unknown, path: string, depth: number) => {
      if (rows.length >= 24 || depth > 4) return;
      if (current === null || ["string", "number", "boolean"].includes(typeof current)) {
        if (!hiddenDetailKey.test(path)) {
          const normalized = current === null ? "null" : String(current);
          rows.push({ key: path || "value", value: normalized.length > 180 ? `${normalized.slice(0, 177)}…` : normalized });
        }
        return;
      }
      if (Array.isArray(current)) {
        current.slice(0, 12).forEach((item, index) => visit(item, `${path}[${index}]`, depth + 1));
        return;
      }
      if (typeof current === "object") {
        Object.entries(current as Record<string, unknown>).forEach(([key, item]) => {
          if (!hiddenDetailKey.test(key)) visit(item, path ? `${path}.${key}` : key, depth + 1);
        });
      }
    };
    visit(parsed, "", 0);
    return rows;
  } catch {
    return [];
  }
}

function mergeEvents(current: PlatformAuditEvent[], incoming: PlatformAuditEvent[]): PlatformAuditEvent[] {
  const seen = new Set(current.map((item) => item.id));
  return [...current, ...incoming.filter((item) => !seen.has(item.id))];
}

function dateLabel(value: string, language: PlatformLanguage): string {
  return new Date(value).toLocaleString(language === "ar" ? "ar-AE" : "en-AE", {
    dateStyle: "medium",
    timeStyle: "medium"
  });
}

export function PlatformAuditPanel({
  token,
  language,
  onCountChange,
  entryFilters
}: {
  token: string;
  language: PlatformLanguage;
  onCountChange?: (count: number) => void;
  entryFilters?: PlatformAuditFilters;
}) {
  const ar = language === "ar";
  const [draft, setDraft] = useState<PlatformAuditFilters>(() => ({ ...initialFilters, ...entryFilters }));
  const [applied, setApplied] = useState<PlatformAuditFilters>(() => ({ ...initialFilters, ...entryFilters }));
  const [items, setItems] = useState<PlatformAuditEvent[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [busy, setBusy] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [diagnostic, setDiagnostic] = useState<PlatformDiagnostic | null>(null);
  const requestSequence = useRef(0);
  const requestController = useRef<AbortController | null>(null);
  const timeZone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone || "Local", []);

  const load = useCallback(async (
    mode: "replace" | "append",
    filters: PlatformAuditFilters,
    cursor: string | null
  ) => {
    const sequence = ++requestSequence.current;
    requestController.current?.abort();
    const controller = new AbortController();
    requestController.current = controller;
    if (mode === "replace") {
      setBusy(true);
      setDiagnostic(null);
    } else {
      setLoadingMore(true);
    }

    try {
      const page = await getPlatformAuditEvents(token, filters, cursor, controller.signal);
      if (sequence !== requestSequence.current) return;
      setItems((current) => mode === "replace" ? page.items : mergeEvents(current, page.items));
      setNextCursor(page.nextCursor);
      setHasMore(page.hasMore);
    } catch (caught) {
      if (caught instanceof DOMException && caught.name === "AbortError") return;
      if (sequence !== requestSequence.current) return;
      setDiagnostic(toPlatformDiagnostic(caught, language, "audit"));
      if (mode === "replace") {
        setItems([]);
        setNextCursor(null);
        setHasMore(false);
      }
    } finally {
      if (sequence === requestSequence.current) {
        setBusy(false);
        setLoadingMore(false);
      }
    }
  }, [language, token]);

  useEffect(() => {
    void load("replace", applied, null);
    return () => requestController.current?.abort();
  }, [applied, load]);

  useEffect(() => {
    onCountChange?.(items.length);
  }, [items.length, onCountChange]);

  useEffect(() => {
    const refreshAfterMutation = () => void load("replace", applied, null);
    window.addEventListener(auditRefreshEvent, refreshAfterMutation);
    return () => window.removeEventListener(auditRefreshEvent, refreshAfterMutation);
  }, [applied, load]);

  const applyFilters = (event: FormEvent) => {
    event.preventDefault();
    setApplied({ ...draft, pageSize: draft.pageSize ?? 30 });
  };

  const clearFilters = () => {
    const cleared = { ...initialFilters };
    setDraft(cleared);
    setApplied(cleared);
  };

  return (
    <section className="platform-panel platform-audit" data-testid="platform-operational-audit">
      <div className="platform-audit__heading">
        <div className="platform-panel__title">
          <div>
            <h2>{ar ? "سجل تدقيق المنصة التشغيلي" : "Operational Platform audit"}</h2>
            <p>{ar
              ? "أحداث منقحة من Control Plane مع pagination حتمية وبيانات تشخيص آمنة."
              : "Redacted control-plane events with deterministic pagination and safe diagnostics."}</p>
          </div>
        </div>
        <div className="platform-audit__timezone">
          <span>{ar ? "الوقت المحلي" : "Local time"}</span>
          <bdi dir="ltr">{timeZone}</bdi>
        </div>
      </div>

      <form className="platform-audit__filters" onSubmit={applyFilters}>
        <Field label={ar ? "المستخدم المنفذ (UUID)" : "Actor user (UUID)"} value={draft.actorUserId ?? ""} onChange={(event) => setDraft((value) => ({ ...value, actorUserId: event.target.value }))} />
        <Field label={ar ? "الإجراء" : "Action"} value={draft.action ?? ""} onChange={(event) => setDraft((value) => ({ ...value, action: event.target.value }))} placeholder="platform.identity.session.revoked" />
        <Field label={ar ? "نوع الكيان" : "Entity type"} value={draft.entityType ?? ""} onChange={(event) => setDraft((value) => ({ ...value, entityType: event.target.value }))} placeholder="PlatformSession" />
        <Field label={ar ? "معرّف الكيان" : "Entity ID"} value={draft.entityId ?? ""} onChange={(event) => setDraft((value) => ({ ...value, entityId: event.target.value }))} />
        <Field label={ar ? "المؤسسة (UUID)" : "Tenant (UUID)"} value={draft.tenantId ?? ""} onChange={(event) => setDraft((value) => ({ ...value, tenantId: event.target.value }))} />
        <Field type="datetime-local" label={ar ? "من" : "From"} value={draft.from ?? ""} onChange={(event) => setDraft((value) => ({ ...value, from: event.target.value }))} />
        <Field type="datetime-local" label={ar ? "إلى" : "To"} value={draft.to ?? ""} onChange={(event) => setDraft((value) => ({ ...value, to: event.target.value }))} />
        <SelectField label={ar ? "حجم الصفحة" : "Page size"} value={String(draft.pageSize ?? 30)} onChange={(event) => setDraft((value) => ({ ...value, pageSize: Number(event.target.value) }))}>
          <option value="20">20</option>
          <option value="30">30</option>
          <option value="50">50</option>
          <option value="100">100</option>
        </SelectField>
        <div className="platform-audit__filter-actions">
          <Button type="submit" disabled={busy || loadingMore}>{ar ? "تطبيق الفلاتر" : "Apply filters"}</Button>
          <Button type="button" variant="secondary" disabled={busy || loadingMore} onClick={clearFilters}>{ar ? "مسح" : "Clear"}</Button>
          <Button type="button" variant="secondary" disabled={busy || loadingMore} onClick={() => void load("replace", applied, null)}>{ar ? "تحديث" : "Refresh"}</Button>
        </div>
      </form>

      {diagnostic ? <PlatformDiagnosticAlert diagnostic={diagnostic} language={language} className="platform-audit__error" /> : null}

      {busy ? (
        <div className="platform-audit__loading"><Spinner label={ar ? "جارٍ تحميل سجل التدقيق" : "Loading audit trail"} /></div>
      ) : items.length === 0 ? (
        <div className="platform-audit__empty" role="status">{ar ? "لا توجد أحداث تطابق الفلاتر الحالية." : "No events match the current filters."}</div>
      ) : (
        <div className="platform-audit-list" aria-live="polite">
          {items.map((item) => {
            const details = detailRows(item.dataRedactedJson);
            return (
              <article key={item.id} data-testid="platform-audit-event">
                <div className="platform-audit-event__identity">
                  <div className="platform-audit-event__badges">
                    <StatusBadge tone="info"><bdi dir="ltr">{item.action}</bdi></StatusBadge>
                    <StatusBadge tone={resultTone(item.result)}>{item.result ?? (ar ? "غير محدد" : "Not specified")}</StatusBadge>
                  </div>
                  <strong>{item.entityType}</strong>
                  <small><bdi dir="ltr">{item.entityId}</bdi></small>
                  {item.tenantId ? <small>Tenant · <bdi dir="ltr">{item.tenantId}</bdi></small> : null}
                </div>

                <dl className="platform-audit-event__metadata">
                  <div>
                    <dt>{ar ? "المنفذ" : "Actor"}</dt>
                    <dd>{item.actorDisplayName ?? (ar ? "النظام" : "System")}</dd>
                    {item.actorEmail ? <small><bdi dir="ltr">{item.actorEmail}</bdi></small> : null}
                    {item.actorUserId ? <small><bdi dir="ltr">{item.actorUserId}</bdi></small> : null}
                  </div>
                  <div>
                    <dt>{ar ? "السبب" : "Reason"}</dt>
                    <dd>{item.reason ?? "—"}</dd>
                  </div>
                  <div>
                    <dt>{ar ? "معرّف الارتباط" : "Correlation ID"}</dt>
                    <dd><bdi dir="ltr">{item.correlationId ?? "—"}</bdi></dd>
                  </div>
                  <div>
                    <dt>{ar ? "الوقت" : "Time"}</dt>
                    <dd><time dateTime={item.occurredAt}>{dateLabel(item.occurredAt, language)}</time></dd>
                    <small><bdi dir="ltr">{new Date(item.occurredAt).toISOString()}</bdi> · UTC</small>
                  </div>
                </dl>

                <details className="platform-audit-event__details">
                  <summary>{ar ? "التفاصيل المنقحة" : "Redacted details"}</summary>
                  {details.length === 0 ? (
                    <p>{ar ? "لا توجد تفاصيل منظمة قابلة للعرض." : "No structured displayable details are available."}</p>
                  ) : (
                    <dl>
                      {details.map((detail, index) => (
                        <div key={`${detail.key}-${index}`}>
                          <dt><bdi dir="ltr">{detail.key}</bdi></dt>
                          <dd><bdi dir="auto">{detail.value}</bdi></dd>
                        </div>
                      ))}
                    </dl>
                  )}
                </details>
              </article>
            );
          })}
        </div>
      )}

      {!busy && items.length > 0 ? (
        <div className="platform-audit__pagination">
          <span>{ar ? `${items.length} حدثًا محملًا` : `${items.length} events loaded`}</span>
          {hasMore && nextCursor ? (
            <Button type="button" variant="secondary" disabled={loadingMore} onClick={() => void load("append", applied, nextCursor)}>
              {loadingMore ? (ar ? "جارٍ التحميل…" : "Loading…") : (ar ? "تحميل المزيد" : "Load more")}
            </Button>
          ) : <span>{ar ? "نهاية النتائج" : "End of results"}</span>}
        </div>
      ) : null}
    </section>
  );
}
